// =============================================================================
// BidSchedulePlanCard — T-347
// On the BID detail page. Lets a manager pencil this bid onto the project
// schedule as a TENTATIVE (soft-red) bar before it's won — opt-in + a date
// window. Only bids ticked here with dates set appear on /schedule, and only
// while the bid is still open/responded.
// =============================================================================

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, AlertCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, Field, Input, Button, Spinner } from "@/components/ui";

export default function BidSchedulePlanCard({ bidId, canEdit }: { bidId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [dirty, setDirty] = useState(false);

  const { data: row, isLoading } = useQuery({
    queryKey: ["bid_schedule_plan", bidId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bids")
        .select("show_on_schedule, tentative_start, tentative_end, status")
        .eq("bid_id", bidId)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });

  useEffect(() => {
    if (!row) return;
    setEnabled(!!row.show_on_schedule);
    setStart(row.tentative_start ?? "");
    setEnd(row.tentative_end ?? "");
    setDirty(false);
  }, [row]);

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("bids")
        .update({
          show_on_schedule: enabled,
          tentative_start:  start || null,
          tentative_end:    end || null,
        })
        .eq("bid_id", bidId);
      if (error) throw error;
    },
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["bid_schedule_plan", bidId] });
      qc.invalidateQueries({ queryKey: ["schedule_tentative_bids"] });
    },
    onError: (e: any) => window.alert(`Couldn't save: ${e?.message ?? String(e)}`),
  });

  const mark = (fn: () => void) => { fn(); setDirty(true); };
  const activeStatus = row && (row.status === "open" || row.status === "responded");
  const needsDates = enabled && (!start || !end);

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <CalendarClock size={14} className="text-text-muted" />
          <span className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Plan on schedule</span>
        </div>
        <p className="text-[12px] text-text-muted mb-3">
          Pencil this bid onto the month schedule as a tentative (soft-red) bar so you can plan
          around potential work before it's won.
        </p>

        {isLoading ? (
          <div className="py-3 flex justify-center"><Spinner size={14} /></div>
        ) : (
          <div className="space-y-3">
            <label className="flex items-start gap-2 cursor-pointer text-[13px]">
              <input
                type="checkbox"
                checked={enabled}
                disabled={!canEdit}
                onChange={e => mark(() => setEnabled(e.target.checked))}
                className="mt-1 accent-accent"
              />
              <span>
                <span className="font-medium">Show on schedule (tentative)</span>
                <span className="block text-[11px] text-text-muted">
                  Appears only while the bid is open/responded. Won → becomes a real project bar; lost → drops off.
                </span>
              </span>
            </label>

            {enabled && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Tentative start">
                  <Input type="date" value={start} disabled={!canEdit} onChange={e => mark(() => setStart(e.target.value))} />
                </Field>
                <Field label="Tentative end" hint="Blank end = single day.">
                  <Input type="date" min={start || undefined} value={end} disabled={!canEdit} onChange={e => mark(() => setEnd(e.target.value))} />
                </Field>
              </div>
            )}

            {enabled && !activeStatus && row && (
              <div className="text-[11px] text-warn inline-flex items-center gap-1.5">
                <AlertCircle size={11} /> This bid is {row.status} — it won't show on the schedule (only open/responded bids do).
              </div>
            )}

            {canEdit && (
              <div className="flex items-center gap-2">
                <Button size="sm" disabled={save.isPending || !dirty || needsDates} onClick={() => save.mutate()}>
                  {save.isPending ? "Saving…" : "Save"}
                </Button>
                {needsDates && <span className="text-[11px] text-text-muted">Set both dates to show it.</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
